import { MDSButton, MDSButtonVariant } from "@/mds";
import { MdsSpacer } from "@/design-system/components/spacer";
import { EventContext } from "@/domains/metrics/context";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { uuidModule } from "@/modules/uuid";
import { AppStore, GuestAppStore } from "@/store";
import { getMentionedNoteIds } from "@/store/note/metadata";
import { NoteMediaKind, NoteModelData, NoteUpsertedSyncUpdateValue } from "@/store/note/types";
import {
  BaseSyncOperationGuestMode,
  BaseSyncOperationGuestModeParams,
} from "@/store/sync/operations/BaseSyncOperationGuestMode";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import { AbstractSyncErrorModalFieldsGenerator } from "@/store/sync/operations/errors/SyncErrorModalFields";
import {
  GuestModeSupportedSyncOperationKind,
  IUpdateNoteContentUsingDiffOperation,
  IUpdateNoteContentUsingDiffOperationGuestMode,
} from "@/store/sync/operations/types";
import { SyncCustomErrorData, SyncModelData, OptimisticSyncUpdate } from "@/store/sync/types";
import { ExtractedNoteMention } from "@mem-labs/common-editor";
import { uniq } from "lodash-es";
import { runInAction } from "mobx";
import { JsonValue } from "type-fest";

export class UpdateNoteContentUsingDiffOperation extends BaseSyncOperationGuestMode<
  IUpdateNoteContentUsingDiffOperation,
  IUpdateNoteContentUsingDiffOperationGuestMode
> {
  primaryLabel: string;
  protected secondaryLabel: string;
  protected mediaKinds?: NoteMediaKind[];
  protected transactionMetadata?: Record<string, JsonValue>;
  protected tiptapJsonContent?: string;
  protected mentions?: ExtractedNoteMention[];
  protected isEmpty?: boolean;

  get operationKind(): GuestModeSupportedSyncOperationKind {
    return "UPDATE_NOTE_CONTENT_USING_DIFF";
  }

  constructor({
    primaryLabel,
    secondaryLabel,
    mediaKinds,
    transactionMetadata,
    tiptapJsonContent,
    mentions,
    isEmpty,
    ...params
  }: {
    primaryLabel: string;
    secondaryLabel: string;
    mediaKinds?: NoteMediaKind[];
    transactionMetadata?: Record<string, JsonValue>;
    tiptapJsonContent?: string;
    mentions?: ExtractedNoteMention[];
    isEmpty?: boolean;
  } & BaseSyncOperationGuestModeParams<IUpdateNoteContentUsingDiffOperation>) {
    super(params);
    this.primaryLabel = primaryLabel;
    this.secondaryLabel = secondaryLabel;
    this.mediaKinds = mediaKinds;
    this.transactionMetadata = transactionMetadata;
    this.tiptapJsonContent = tiptapJsonContent;
    this.mentions = mentions;
    this.isEmpty = isEmpty;
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    // Apply optimistic updates regardless of whether or not there is a contentDiff
    let note;
    if (this.store instanceof GuestAppStore) {
      note = this.store.note.getNoteObservableById({ noteId: this.payload.id });
    } else {
      note = this.store.notes.get(this.payload.id);
    }
    if (!note) return [];

    const modifiedBySpaceAccountIds = [...note.modelData.modified_by_space_account_ids];

    const isSilent = this.payload.is_silent;
    if (!isSilent) {
      modifiedBySpaceAccountIds.push(
        this.store instanceof GuestAppStore
          ? this.store.guestAccount.myPersonalSpaceAccountId
          : this.store.spaceAccounts.myPersonalSpaceAccountId
      );
    }

    const value: NoteUpsertedSyncUpdateValue = {
      model_id: this.payload.id,
      model_kind: "NOTE",
      model_version: note.modelVersion,
      model_data: {
        ...note.modelData,
        primary_label: this.primaryLabel,
        secondary_label: this.secondaryLabel,
        media_kinds: this.mediaKinds || [],
        mentioned_note_ids: getMentionedNoteIds(this.mentions || []),
        locally_modified_at: this.committedAt,
        modified_by_space_account_ids: uniq(modifiedBySpaceAccountIds),
        is_empty: this.isEmpty || false,
      },
      model_scopes: note.modelScopes,
    };

    // HACK: adding is_silent here because NoteIndexes.modified_at needs to ignore locally_modified_at.
    const syncUpdate: OptimisticSyncUpdate<NoteModelData> & { is_silent?: boolean } = {
      optimistic_update_id: uuidModule.generate(),

      is_silent: isSilent,
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value,
    };
    return [syncUpdate];
  }

  async execute(): Promise<void> {
    return super.execute();
  }

  async triggerRecompute() {
    if (this.store instanceof GuestAppStore) return;
    await this.store.notes.recompute(this.payload.id);
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.triggerModal(
      this.getSyncErrorModalFieldsGenerator("INVALID", {
        requestAccessAndSaveEditsInNewNoteButtonsEnabled: true,
      })
    );
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.triggerModal(
      this.getSyncErrorModalFieldsGenerator("PERMISSION_DENIED", {
        requestAccessAndSaveEditsInNewNoteButtonsEnabled: true,
      })
    );
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.triggerModal(
      this.getSyncErrorModalFieldsGenerator("UNKNOWN", {
        requestAccessAndSaveEditsInNewNoteButtonsEnabled: true,
      }),
      SyncErrorHandlingType.RetryWithLimit
    );
  }

  get syncErrorModalFieldsGenerator() {
    return this.getSyncErrorModalFieldsGenerator("UNKNOWN");
  }

  getSyncErrorModalFieldsGenerator(
    kind: SyncCustomErrorData["kind"],
    opts: UpdateNoteContentUsingDiffSyncErrorModalFieldsGeneratorOps = {
      requestAccessAndSaveEditsInNewNoteButtonsEnabled: false,
    }
  ) {
    return (store: AppStore) =>
      new UpdateNoteContentUsingDiffSyncErrorModalFieldsGenerator(
        store,
        this.id,
        this.payload.id,
        kind,
        opts
      );
  }
}

interface UpdateNoteContentUsingDiffSyncErrorModalFieldsGeneratorOps {
  requestAccessAndSaveEditsInNewNoteButtonsEnabled: boolean;
  onAfterClose?: () => void;
}

class UpdateNoteContentUsingDiffSyncErrorModalFieldsGenerator extends AbstractSyncErrorModalFieldsGenerator {
  protected id: string;
  protected noteId: string;
  protected kind: SyncCustomErrorData["kind"];
  protected requestAccessAndSaveEditsInNewNoteButtonsEnabled: boolean;
  protected onAfterClose?: () => void;

  constructor(
    store: AppStore,
    id: string,
    noteId: string,
    kind: SyncCustomErrorData["kind"],
    opts: UpdateNoteContentUsingDiffSyncErrorModalFieldsGeneratorOps
  ) {
    super(store);

    this.id = id;
    this.noteId = noteId;
    this.kind = kind;
    this.requestAccessAndSaveEditsInNewNoteButtonsEnabled =
      opts.requestAccessAndSaveEditsInNewNoteButtonsEnabled;
    this.onAfterClose = opts.onAfterClose;
  }

  reloadEditors() {
    // Any open editor or note viewer will be notified to reload the note without reverted operations.
    this.store.publicAppStore.interface.notifyReloadRequired(this.noteId);
    this.onAfterClose?.();
  }

  requestEditAccess = () => {
    // TODO: https://linear.app/mem-labs/issue/MEM-7702/update-note-contents-permission-denied-request-edit-access
  };

  discardEdits = async () => {
    await this.store.sync.actionQueue.skipAndRevertRelatedOperationsById(this.id);
    runInAction(() => {
      this.reloadEditors();
      this.store.sync.actionQueue.resume();
    });
  };

  saveChangesAsNewNote = async () => {
    // TODO: https://linear.app/mem-labs/issue/MEM-7703/update-note-contents-permission-denied-save-edits-as-new-note
    const note = await this.store.notes.getAsync(this.noteId);
    if (!note) return;
    await note.saveAsNewNote({ eventContext: EventContext.ErrorModal });
    this.discardEdits();
  };

  tryAgain = () => {
    this.store.sync.actionQueue.resume();
  };

  get title() {
    const note = this.store.notes.get(this.noteId);
    const title = note?.title || UNTITLED_NOTE_TITLE;
    return `Having trouble saving your “${title}”`;
  }

  get message() {
    switch (this.kind) {
      case "INVALID":
      case "UNKNOWN": {
        return `Trying again usually solves this issue. If this issue continues to appear, you can save your changes as a new note instead.`;
      }
      case "PERMISSION_DENIED": {
        return (
          <>
            You no longer have edit access to this note and your changes could not be saved. Contact
            the note owner, or <span onClick={this.requestEditAccess}>request edit access</span>,
            then try again.
          </>
        );
      }
    }
  }

  get resetActionLabel() {
    return this.requestAccessAndSaveEditsInNewNoteButtonsEnabled ? null : "Got it";
  }

  get extraActionButtons() {
    if (!this.requestAccessAndSaveEditsInNewNoteButtonsEnabled) return;

    return (
      <>
        <MDSButton onClick={this.discardEdits}>Discard edits</MDSButton>
        <MdsSpacer />
        <MDSButton onClick={this.saveChangesAsNewNote}>Save changes as new note</MDSButton>
        <MDSButton variant={MDSButtonVariant.Contrast} onClick={this.tryAgain}>
          Try again
        </MDSButton>
      </>
    );
  }

  modalActionHandler = async () => {
    await this.store.sync.actionQueue.skipAndRevertRelatedOperationsById(this.id);
    this.reloadEditors();
  };
}
